// client/src/components/Admin/InstitutionsReviCmp/InstitutionReviFormModal.js
//
// Modal con form de alta/edicion de InstitutionRevi para el panel admin
// /admin/revi tab Instituciones. AntD Form + Upload de avatar.
//
// El backend (controllers/revi/institutionRevi.js):
// - createInstitutionRevi requiere institutionName, email, password,
//   partnerId, phone, address, city, country. Plan opcional pero si se
//   asigna descuenta una activacion del partner.
// - updateInstitutionRevi acepta los mismos campos como partial.

import React, { useEffect, useMemo } from "react";
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Upload,
  Avatar,
  Button,
  Switch,
  Row,
  Col,
} from "antd";
import { UploadOutlined, UserOutlined } from "@ant-design/icons";

export function InstitutionReviFormModal({
  open,
  onClose,
  onSubmit,
  institution,
  partners,
  plans,
  submitting,
}) {
  const [form] = Form.useForm();
  const isEdit = Boolean(institution);

  // Reset / prefill cuando cambia institucion o se abre el modal
  useEffect(() => {
    if (!open) return;
    if (institution) {
      form.setFieldsValue({
        institutionName: institution.institutionName || "",
        email: institution.email || "",
        password: "",
        partnerId:
          (institution.partner && institution.partner._id) ||
          institution.partner ||
          undefined,
        planEducaId:
          (institution.planEduca && institution.planEduca._id) ||
          institution.planEduca ||
          undefined,
        pacientesN: institution.pacientesN || 0,
        terapeutasN: institution.terapeutasN || 0,
        address: institution.address || "",
        city: institution.city || "",
        country: institution.country || "",
        phone: institution.phone || "",
        website: institution.website || "",
        active: institution.active !== false,
        avatarFile: undefined,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ active: true, pacientesN: 0, terapeutasN: 0 });
    }
  }, [open, institution, form]);

  const planOptions = useMemo(
    () =>
      (plans || []).map((p) => ({
        value: p._id,
        label: p.title,
        patientsNumber: p.patientsNumber,
        therapistsNumber: p.therapistsNumber,
      })),
    [plans],
  );

  const partnerOptions = useMemo(
    () =>
      (partners || []).map((u) => ({
        value: u._id,
        label:
          `${u.firstname || ""} ${u.lastname || ""}`.trim() || u.email || u._id,
        email: u.email,
      })),
    [partners],
  );

  const handlePlanChange = (planId) => {
    if (!planId) return;
    const sel = planOptions.find((o) => o.value === planId);
    if (sel) {
      form.setFieldsValue({
        pacientesN: sel.patientsNumber || 0,
        terapeutasN: sel.therapistsNumber || 0,
      });
    }
  };

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      // El partnerId no se modifica en edicion (el backend no lo soporta
      // y cambiar el dueno requiere otra operacion). Solo lo enviamos en
      // alta.
      const payload = { ...values };
      if (isEdit) {
        delete payload.partnerId;
        // En edicion, password vacia = no cambiar
        if (!payload.password) delete payload.password;
      }
      await onSubmit(payload);
    } catch (err) {
      if (err?.errorFields) return;
      throw err;
    }
  };

  const avatarPreview = Form.useWatch("avatarFile", form);
  const previewUrl = avatarPreview ? URL.createObjectURL(avatarPreview) : null;
  const currentAvatar = previewUrl || institution?.avatar || null;

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={handleOk}
      okText={isEdit ? "Guardar" : "Crear"}
      cancelText="Cancelar"
      title={isEdit ? "Editar institucion REVI" : "Nueva institucion REVI"}
      width={760}
      confirmLoading={submitting}
      destroyOnHidden
    >
      <Form form={form} layout="vertical" requiredMark>
        <Row gutter={16}>
          <Col span={6} style={{ textAlign: "center" }}>
            <Avatar
              size={96}
              src={currentAvatar}
              icon={!currentAvatar ? <UserOutlined /> : undefined}
            />
            <Form.Item name="avatarFile" style={{ marginTop: 12 }}>
              <Upload
                beforeUpload={(file) => {
                  form.setFieldsValue({ avatarFile: file });
                  return false;
                }}
                maxCount={1}
                accept="image/png,image/jpeg"
                showUploadList={false}
              >
                <Button icon={<UploadOutlined />} size="small">
                  Subir imagen
                </Button>
              </Upload>
            </Form.Item>
          </Col>
          <Col span={18}>
            <Form.Item
              name="institutionName"
              label="Nombre del centro"
              rules={[{ required: true, message: "Obligatorio" }]}
            >
              <Input placeholder="Residencia Altavida" />
            </Form.Item>
            <Row gutter={12}>
              <Col span={12}>
                <Form.Item
                  name="email"
                  label="Email de acceso"
                  rules={[
                    { required: true, message: "Obligatorio" },
                    { type: "email", message: "Email invalido" },
                  ]}
                >
                  <Input placeholder="centro@dominio.org" />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name="password"
                  label={isEdit ? "Nueva contrasena (opcional)" : "Contrasena"}
                  rules={
                    isEdit
                      ? []
                      : [{ required: true, message: "Obligatoria al crear" }]
                  }
                >
                  <Input.Password
                    placeholder={isEdit ? "Dejar en blanco" : ""}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item
              name="partnerId"
              label="Partner"
              rules={
                isEdit
                  ? []
                  : [{ required: true, message: "Selecciona un partner" }]
              }
            >
              <Select
                placeholder="Selecciona el partner propietario"
                showSearch
                disabled={isEdit}
                optionFilterProp="label"
                options={partnerOptions}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="planEducaId" label="Plan contratado">
              <Select
                allowClear
                placeholder="Sin plan asignado"
                onChange={handlePlanChange}
                options={planOptions}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="pacientesN" label="Licencias pacientes">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="terapeutasN" label="Licencias terapeutas">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="address" label="Direccion">
          <Input />
        </Form.Item>
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="city" label="Ciudad">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="country" label="Pais">
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="phone" label="Telefono">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={12}>
          <Col span={16}>
            <Form.Item name="website" label="Sitio web">
              <Input placeholder="https://..." />
            </Form.Item>
          </Col>
          {isEdit && (
            <Col span={8}>
              <Form.Item name="active" label="Activa" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          )}
        </Row>
      </Form>
    </Modal>
  );
}
